import AuthService from './AuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8082/api';

// Helper function to get headers with auth
const getHeaders = (includeContentType = true) => {
  const headers = {};
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  const authHeader = AuthService.getAuthHeader();
  if (authHeader) {
    headers['Authorization'] = authHeader;
  }
  return headers;
};

/**
 * Handle API response and check for authentication errors.
 * Dispatches a custom event on 401 Unauthorized to trigger logout.
 */
const handleResponse = async (response) => {
  if (response.status === 401) {
    console.warn('ChatService: 401 Unauthorized - session expired or invalid');
    // Dispatch custom event for AuthContext to handle
    window.dispatchEvent(new CustomEvent('auth:unauthorized', {
      detail: { message: 'Session expired. Please login again.' }
    }));
    const authError = new Error('Session expired. Please login again.');
    authError.isBackendResponse = true;
    authError.status = 401;
    throw authError;
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Reaching this point means the backend WAS reached and returned a real,
    // specific error (e.g. ChatController's 403 "Start a new conversation for
    // the selected facility" for a session/facility mismatch) -- distinct
    // from a true network/transport failure (fetch rejecting before any
    // response at all). Callers use this flag to show the real backend
    // message instead of treating it as "backend is down." See AtomApp.js's
    // handleSendMessage catch block.
    const backendError = new Error(errorData.response || errorData.message || `HTTP error! status: ${response.status}`);
    backendError.isBackendResponse = true;
    backendError.status = response.status;
    throw backendError;
  }

  return response;
};

// ==================== Live activity of a chat turn ====================
//
// Every chat request names its turn (X-Chat-Turn-Id); the backend uses that id for the turn's orchestration trace.
// While the request is out, GET /chat/turns/{id} is polled -- each tool Atom calls and the FLEX Commands under it,
// running, done or failed -- and announced as a window event 'chat:turn' ({turnId, status, steps, elapsedSeconds}),
// so any chat screen can show it (ChatTurnActivity). When the browser's own request is lost (a proxy's 502/503/504,
// or the network drops) the work goes on on the server: polling continues, and when the turn is over its reply is
// taken from the conversation.

export const CHAT_TURN_EVENT = 'chat:turn';
const TURN_POLL_MS = 1200;
const LOST_POLL_MS = 2500;
const TURN_GIVE_UP_MS = 30 * 60 * 1000;
const ALNUM = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';

// 26 letters and digits, like the server's own trace ids
const newTurnId = () => {
  const bytes = new Uint8Array(18);
  if (window.crypto && window.crypto.getRandomValues) window.crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  const time = Date.now().toString(36).toUpperCase().padStart(8, '0').slice(-8);
  return time + Array.from(bytes, (b) => ALNUM[b % ALNUM.length]).join('');
};

const announceTurn = (detail) => window.dispatchEvent(new CustomEvent(CHAT_TURN_EVENT, { detail }));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// The request was lost on the way back, not refused by the backend: a proxy timeout or a dropped connection.
const lostOnTheWay = (error) => !error.isBackendResponse || [502, 503, 504].includes(error.status);

// The turn as the backend sees it: null when it knows no such turn (yet), undefined when there was no answer.
const readTurn = async (turnId) => {
  const res = await fetch(`${API_BASE_URL}/chat/turns/${turnId}`, { headers: getHeaders(false) });
  if (res.status === 404) return null;
  if (!res.ok) return undefined;
  return res.json();
};

// The reply a finished turn left in its conversation: the last answer from Atom since the turn began.
const replyOfTurn = async (turn) => {
  if (!turn.sessionId) return null;
  const res = await fetch(`${API_BASE_URL}/sessions/${turn.sessionId}/messages`, { headers: getHeaders(false) });
  if (!res.ok) return null;
  const messages = await res.json();
  const since = Date.parse(turn.startedAt) - 5000;
  // a message's timestamp is epoch milliseconds (older rows: date text)
  const when = (m) => (typeof m.timestamp === 'number' ? m.timestamp : Date.parse(m.timestamp));
  const answer = [...(messages || [])].reverse()
    .find((m) => m.role === 'assistant' && (!m.timestamp || !(when(m) < since)));
  if (!answer) return null;
  return { response: answer.content, sessionId: turn.sessionId, ...(answer.metadata || {}), recoveredFromTurn: true };
};

/**
 * Runs one chat request as a turn: sends it with its turn id, announces what Atom is doing while it runs, and when
 * the request itself is lost keeps following the turn until its reply is in the conversation.
 */
const runTurn = async (send) => {
  const turnId = newTurnId();
  const startedAt = Date.now();
  let latest = { turnId, status: 'RUNNING', steps: [], elapsedSeconds: 0, startedAt };
  let settled = false;
  const update = (data, extra = {}) => {
    if (data) latest = { ...latest, ...data, startedAt };
    announceTurn({ ...latest, ...extra });
  };

  const poller = (async () => {
    while (!settled) {
      await sleep(TURN_POLL_MS);
      if (settled) break;
      try {
        const data = await readTurn(turnId);
        if (!settled && data) update(data);
      } catch (e) {
        // a missed poll is fine; the next one catches up
      }
    }
  })();

  update(null);
  try {
    const reply = await send({ 'X-Chat-Turn-Id': turnId });
    settled = true;
    update(null, { status: 'DONE' });
    return reply;
  } catch (error) {
    settled = true;
    if (!lostOnTheWay(error)) {
      update(null, { status: 'FAILED', error: error.message });
      throw error;
    }
    // The answer did not come back, but Atom may still be working: follow the turn.
    await poller;
    update(null, { reconnecting: true });
    let unknownSince = null;
    let everSeen = latest.steps.length > 0;
    while (Date.now() - startedAt < TURN_GIVE_UP_MS) {
      let data;
      try {
        data = await readTurn(turnId);
      } catch (e) {
        data = undefined;
      }
      if (data === null) {
        // the request never reached the backend, or the backend restarted
        unknownSince = unknownSince || Date.now();
        if (Date.now() - unknownSince > 30000) break;
      } else if (data) {
        unknownSince = null;
        everSeen = true;
        if (data.status === 'DONE') {
          let reply = null;
          try {
            reply = await replyOfTurn({ ...data, startedAt: data.startedAt || new Date(startedAt).toISOString() });
          } catch (e) {
            reply = null;
          }
          update(data, { status: 'DONE' });
          if (reply) return reply;
          const noReply = new Error('Atom finished, but its answer did not reach this screen. Open this conversation again to see it.');
          noReply.isBackendResponse = true;
          throw noReply;
        }
        update(data, { reconnecting: true });
      }
      await sleep(LOST_POLL_MS);
    }
    update(null, { status: 'FAILED', error: 'lost' });
    if (!everSeen) {
      const notArrived = new Error('Atom did not receive this message (the connection failed). Please send it again.');
      notArrived.isBackendResponse = true;
      throw notArrived;
    }
    const lost = new Error('The connection to Atom was lost before the answer came back. Atom may have finished the work: look at the screens, or ask Atom what was done.');
    lost.isBackendResponse = true;
    throw lost;
  }
};

/**
 * ChatService - Handles chat sessions, messages, wizard forms, and feedback.
 *
 * For other functionality, use the specialized services:
 * - AdminService: User management, roles, connected systems
 * - TriggerService: Event triggers, executions, logs
 * - PromptService: Prompt library, versions, test runs
 * - DocumentService: Document upload, search, management
 * - LlmService: LLM playground, tools, API proxy
 * - FlexService: FLEX scripts, APIs, tools
 */
class ChatService {

  // ==================== Chat Messages ====================

  /**
   * Send a chat message to the backend.
   *
   * @param {string} message - The user's message
   * @param {string} userId - User ID
   * @param {string} sessionId - Session ID (optional)
   * @param {string} connectedSystemId - Connected system ID (optional)
   * @param {Object} options - Additional options
   * @param {string} options.llmMode - LLM processing mode: 'AUTO' (default), 'DIRECT_TOOL', 'NO_LLM'
   * @param {string} options.toolKey - Specific tool to execute (for DIRECT_TOOL mode)
   * @param {Object} options.toolParameters - Pre-filled parameters for the tool
   */
  static async sendMessage(message, userId, sessionId = null, connectedSystemId = null, options = {}) {
    try {
      const requestBody = {
        message,
        userId,
        sessionId,
        connectedSystemId,
      };

      // Add workspace context if provided
      if (options.workspaceContext) {
        requestBody.workspaceContext = options.workspaceContext;
      }

      // Add optional LLM mode parameters
      if (options.llmMode) {
        requestBody.llmMode = options.llmMode;
      }
      if (options.toolKey) {
        requestBody.toolKey = options.toolKey;
      }
      if (options.toolParameters) {
        requestBody.toolParameters = options.toolParameters;
      }

      return await runTurn(async (turnHeaders) => {
        const response = await fetch(`${API_BASE_URL}/chat`, {
          method: 'POST',
          headers: { ...getHeaders(), ...turnHeaders },
          body: JSON.stringify(requestBody),
        });
        await handleResponse(response);
        return response.json();
      });
    } catch (error) {
      console.error('Error in ChatService:', error);
      throw error;
    }
  }

  /**
   * Send a chat message with one or more attached files (Phase 1: CSV/plain
   * text only — decoded and inlined into the LLM's context on the backend,
   * for ad-hoc analysis using the message text as the question. Not for
   * document/knowledge-base upload (see DocumentService for that) and not
   * for the Area bulk-import wizard (a separate, deterministic flow).
   *
   * @param {string} message - The user's message/question about the file(s)
   * @param {string} userId - User ID
   * @param {string} sessionId - Session ID (optional)
   * @param {string} connectedSystemId - Connected system ID (optional)
   * @param {File[]} files - Attached files (browser File objects)
   * @param {Object} options - Same shape as sendMessage's options
   */
  static async sendMessageWithFiles(message, userId, sessionId = null, connectedSystemId = null, files = [], options = {}) {
    try {
      const formData = new FormData();
      formData.append('message', message || '');
      if (userId) formData.append('userId', userId);
      if (sessionId) formData.append('sessionId', sessionId);
      if (connectedSystemId) formData.append('connectedSystemId', connectedSystemId);
      if (options.llmMode) formData.append('llmMode', options.llmMode);
      files.forEach((file) => formData.append('files', file));

      // No Content-Type header — the browser sets the multipart boundary itself.
      return await runTurn(async (turnHeaders) => {
        const response = await fetch(`${API_BASE_URL}/chat/upload`, {
          method: 'POST',
          headers: { ...getHeaders(false), ...turnHeaders },
          body: formData,
        });
        await handleResponse(response);
        return response.json();
      });
    } catch (error) {
      console.error('Error in ChatService (sendMessageWithFiles):', error);
      throw error;
    }
  }

  /**
   * Submit wizard form data to the backend.
   * Used for structured wizard form submissions with specific actions.
   *
   * @param {Object} params - Form submission parameters
   * @param {string} params.userId - User ID submitting the form
   * @param {string} params.sessionId - Chat session ID
   * @param {string} params.connectedSystemId - Connected system ID for context
   * @param {string} params.action - Action to perform: 'submit', 'add_line', 'edit_line', 'remove_line', 'done_adding', 'cancel'
   * @param {Object} params.fieldValues - Form field values as key-value pairs
   * @returns {Promise<Object>} Chat response with wizard state
   */
  static async submitWizardForm({ userId, sessionId, connectedSystemId, action, fieldValues }) {
    try {
      return await runTurn(async (turnHeaders) => {
        const response = await fetch(`${API_BASE_URL}/chat/wizard`, {
          method: 'POST',
          headers: { ...getHeaders(), ...turnHeaders },
          body: JSON.stringify({
            userId,
            sessionId,
            connectedSystemId,
            action,
            fieldValues
          }),
        });
        await handleResponse(response);
        return response.json();
      });
    } catch (error) {
      console.error('Error submitting wizard form:', error);
      throw error;
    }
  }

  // ==================== Sessions ====================

  static async getSessions() {
    try {
      const response = await fetch(`${API_BASE_URL}/sessions`, {
        headers: getHeaders(false),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting sessions:', error);
      throw error;
    }
  }

  static async getSession(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
        headers: getHeaders(false),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting session:', error);
      throw error;
    }
  }

  static async getSessionMessages(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}/messages`, {
        headers: getHeaders(false),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error getting session messages:', error);
      throw error;
    }
  }

  static async deleteSession(sessionId) {
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return true;
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error;
    }
  }

  static async updateSessionDescription(sessionId, description) {
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/${sessionId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({ description }),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating session description:', error);
      throw error;
    }
  }

  static async searchConversations(query) {
    try {
      const response = await fetch(`${API_BASE_URL}/sessions/search?query=${encodeURIComponent(query)}`, {
        headers: getHeaders(false),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error searching conversations:', error);
      throw error;
    }
  }

  // ==================== Health & Quick Actions ====================

  static async checkHealth() {
    try {
      const response = await fetch(`${API_BASE_URL}/chat/health`, {
        headers: getHeaders(false),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Error checking health:', error);
      throw error;
    }
  }

  /**
   * Get context-aware quick action suggestions from the backend
   * @param {string} sessionId - Optional session ID for context-aware suggestions
   * @returns {Promise<Array>} Array of quick action objects with buttonText and sentence
   */
  static async getQuickActions(sessionId = null) {
    try {
      const url = sessionId
        ? `${API_BASE_URL}/suggestions?sessionId=${encodeURIComponent(sessionId)}`
        : `${API_BASE_URL}/suggestions`;
      const response = await fetch(url, {
        headers: getHeaders(false),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.quickActions || data;
    } catch (error) {
      console.error('Error getting quick actions:', error);
      throw error;
    }
  }

  // ==================== Feedback ====================

  /**
   * Send feedback for a message to enable continuous learning
   * @param {Object} feedback - Feedback data
   * @param {string} feedback.messageId - The ID of the message being rated
   * @param {string} feedback.sessionId - The session ID (optional)
   * @param {string} feedback.type - Feedback type: 'positive', 'negative', or 'flag'
   * @param {string} feedback.comment - Optional detailed feedback text
   * @param {string} feedback.userMessage - The original user message (for context)
   * @param {string} feedback.botResponse - The bot response being rated (for context)
   */
  static async sendFeedback(feedback) {
    try {
      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          messageId: feedback.messageId,
          sessionId: feedback.sessionId || null,
          feedbackType: feedback.type,
          comment: feedback.comment || null,
          userMessage: feedback.userMessage || null,
          botResponse: feedback.botResponse || null,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending feedback:', error);
      // Don't throw - feedback should fail silently to not disrupt user experience
      return { success: false, error: error.message };
    }
  }

  // ==================== Quick Menu Items ====================

  /**
   * Get all quick menu items for the current user
   * @returns {Promise<Array>} List of quick menu items
   */
  static async getQuickMenuItems() {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/preferences/quick-menu`, {
        method: 'GET',
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting quick menu items:', error);
      throw error;
    }
  }

  /**
   * Add a new quick menu item
   * @param {string} value - The prompt text
   * @param {string} displayValue - The button label (optional)
   * @returns {Promise<Object>} Result with success flag and item
   */
  static async addQuickMenuItem(value, displayValue = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/preferences/quick-menu`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({ value, displayValue }),
      });

      return await response.json();
    } catch (error) {
      console.error('Error adding quick menu item:', error);
      throw error;
    }
  }

  /**
   * Update a quick menu item
   * @param {string} itemId - The item ID
   * @param {Object} updates - Object with value and/or displayValue
   * @returns {Promise<Object>} Result with success flag and updated item
   */
  static async updateQuickMenuItem(itemId, updates) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/preferences/quick-menu/${encodeURIComponent(itemId)}`, {
        method: 'PUT',
        headers: getHeaders(true),
        body: JSON.stringify(updates),
      });

      return await response.json();
    } catch (error) {
      console.error('Error updating quick menu item:', error);
      throw error;
    }
  }

  /**
   * Delete a quick menu item
   * @param {string} itemId - The item ID
   * @returns {Promise<Object>} Result with success flag
   */
  static async deleteQuickMenuItem(itemId) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/preferences/quick-menu/${encodeURIComponent(itemId)}`, {
        method: 'DELETE',
        headers: getHeaders(false),
      });

      return await response.json();
    } catch (error) {
      console.error('Error deleting quick menu item:', error);
      throw error;
    }
  }

  /**
   * Record that a saved prompt was actually used (clicked into the input or
   * sent directly) -- bumps usage_count server-side, which drives the
   * top-10 "most used" default view. Fire-and-forget: failures are logged
   * but never surfaced, since this must never block the click it's tracking.
   * @param {string} itemId - The item ID
   */
  static async recordQuickMenuItemUse(itemId) {
    try {
      await fetch(`${API_BASE_URL}/v1/preferences/quick-menu/${encodeURIComponent(itemId)}/use`, {
        method: 'POST',
        headers: getHeaders(false),
      });
    } catch (error) {
      console.warn('Error recording quick menu item use (non-blocking):', error);
    }
  }

  /**
   * Reorder quick menu items
   * @param {Array<string>} itemIds - Ordered list of item IDs
   * @returns {Promise<Object>} Result with success flag
   */
  static async reorderQuickMenuItems(itemIds) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/preferences/quick-menu/reorder`, {
        method: 'PUT',
        headers: getHeaders(true),
        body: JSON.stringify({ itemIds }),
      });

      return await response.json();
    } catch (error) {
      console.error('Error reordering quick menu items:', error);
      throw error;
    }
  }

  /**
   * Get field metadata for an entity (used to list available grid columns).
   * @param {string} entityType - The entity name (e.g. "Order")
   * @returns {Promise<Array>} List of field metadata objects
   */
  static async getEntityFields(entityType) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/catalog/${encodeURIComponent(entityType)}/fields`, {
        method: 'GET',
        headers: getHeaders(false),
      });
      const data = await response.json();
      return data.fields || [];
    } catch (error) {
      console.error('Error getting entity fields:', error);
      return [];
    }
  }

  /**
   * Get the current user's saved grid column config (visibility/order) for an entity.
   * @param {string} entityType - The entity name (e.g. "Order")
   * @returns {Promise<Array|null>} Saved column list, or null if never customized
   */
  static async getColumnConfig(entityType) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/preferences/column-config/${encodeURIComponent(entityType)}`, {
        method: 'GET',
        headers: getHeaders(false),
      });
      const data = await response.json();
      return data.columns || null;
    } catch (error) {
      console.error('Error getting column config:', error);
      return null;
    }
  }

  /**
   * Save the current user's grid column config for an entity.
   * @param {string} entityType - The entity name (e.g. "Order")
   * @param {Array<{fieldName: string, visible: boolean, order: number}>} columns
   * @returns {Promise<Object>} Result with success flag
   */
  static async saveColumnConfig(entityType, columns) {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/preferences/column-config/${encodeURIComponent(entityType)}`, {
        method: 'PUT',
        headers: getHeaders(true),
        body: JSON.stringify({ columns }),
      });

      return await response.json();
    } catch (error) {
      console.error('Error saving column config:', error);
      throw error;
    }
  }
}

export default ChatService;
